import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft, Send, Calculator } from 'lucide-react';
import axios from 'axios';
import { useLoadScript, Autocomplete } from '@react-google-maps/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const libraries = ['places'];

const availableVehicles = [
  { name: 'Lexus ES300h', basePrice: 8.0, pricePerKm: 1.50, passengers: 4, luggage: 4, luggageDesc: '2 grands + 2 petits bagages' },
  { name: 'Mercedes E-Class', basePrice: 8.0, pricePerKm: 1.50, passengers: 4, luggage: 4, luggageDesc: '2 grands + 2 petits bagages' },
  { name: 'BMW Série 5', basePrice: 8.0, pricePerKm: 1.50, passengers: 4, luggage: 4, luggageDesc: '2 grands + 2 petits bagages' },
  { name: 'Mercedes E-Class Break', basePrice: 8.0, pricePerKm: 1.50, passengers: 4, luggage: 4, luggageDesc: '2 grands + 2 petits bagages (Extended)' }
];

const AdminCreateQuote = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  
  // Form state
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [pickup, setPickup] = useState('');
  const [dropoff, setDropoff] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [tripType, setTripType] = useState('one-way');
  const [returnDate, setReturnDate] = useState('');
  const [returnTime, setReturnTime] = useState('');
  const [vehicle, setVehicle] = useState('Lexus ES300h');
  const [passengers, setPassengers] = useState(4);
  const [luggage, setLuggage] = useState(4);
  const [specialNotes, setSpecialNotes] = useState('');
  const [validityDays, setValidityDays] = useState(7);
  const [customValidity, setCustomValidity] = useState(false);
  
  // Base fare options
  const [baseFareType, setBaseFareType] = useState('standard');
  const [customBaseFare, setCustomBaseFare] = useState(8.0);
  const [baseFareReason, setBaseFareReason] = useState('');
  
  // Pricing state
  const [distance, setDistance] = useState(0);
  const [pricing, setPricing] = useState({
    basePrice: 0,
    distancePrice: 0,
    optionsPrice: 0,
    discount: 0,
    total: 0
  });
  
  // Autocomplete refs
  const [pickupAutocomplete, setPickupAutocomplete] = useState(null);
  const [dropoffAutocomplete, setDropoffAutocomplete] = useState(null);
  
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries
  });
  
  // Helper function to get base fare label
  const getBaseFareLabel = (type) => {
    switch(type) {
      case 'standard':
        return 'Standard Bruxelles';
      case 'enlarged':
        return 'Zone élargie - Brabant wallon/flamand';
      case 'outOfZone':
        return 'Hors zone - Flandre/Wallonie';
      case 'custom':
        return 'Personnalisé';
      default:
        return 'Standard Bruxelles';
    }
  };
  
  const getBaseFareExplanation = (type) => {
    switch(type) {
      case 'enlarged':
        return 'Frais de déplacement jusqu\'au point de départ inclus';
      case 'outOfZone':
        return 'Frais de déplacement longue distance inclus';
      default:
        return '';
    }
  };
  
  useEffect(() => {
    const adminUser = localStorage.getItem('adminUser');
    if (!adminUser) {
      navigate('/admin/login');
    }
  }, [navigate]);
  
  // Calculate pricing whenever relevant fields change
  useEffect(() => {
    if (pickup && dropoff && vehicle && distance > 0) {
      calculatePrice();
    }
  }, [pickup, dropoff, vehicle, distance, tripType, baseFareType, customBaseFare]);
  
  const calculatePrice = () => {
    const selectedVehicle = availableVehicles.find(v => v.name === vehicle);
    if (!selectedVehicle) return;
    
    // Get base fare based on selection
    let basePrice = 8.0; // default
    if (baseFareType === 'standard') {
      basePrice = 8.0;
    } else if (baseFareType === 'enlarged') {
      basePrice = 30.0;
    } else if (baseFareType === 'outOfZone') {
      basePrice = 50.0;
    } else if (baseFareType === 'custom') {
      basePrice = parseFloat(customBaseFare) || 8.0;
    }
    
    const distancePrice = distance * selectedVehicle.pricePerKm;
    let discount = 0;
    
    // Apply round-trip discount (10%)
    if (tripType === 'round-trip') {
      discount = (basePrice + distancePrice) * 0.1;
    }
    
    const total = basePrice + distancePrice - discount;
    
    setPricing({
      basePrice,
      distancePrice,
      optionsPrice: 0,
      discount,
      total: Math.max(0, total)
    });
  };
  
  const calculateDistance = async () => {
    if (!pickup || !dropoff) {
      toast.error('Veuillez entrer les adresses de départ et d\'arrivée');
      return;
    }
    
    setCalculating(true);
    
    try {
      const response = await axios.post(`${API}/calculate-distance`, {
        origin: pickup,
        destination: dropoff
      });
      
      if (response.data.distance_km) {
        setDistance(response.data.distance_km);
        toast.success(`Distance calculée : ${response.data.distance_km.toFixed(1)} km`);
      }
    } catch (error) {
      console.error('Error calculating distance:', error);
      toast.error('Erreur lors du calcul de la distance');
    } finally {
      setCalculating(false);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!customerName || !customerEmail || !customerPhone) {
      toast.error('Veuillez remplir toutes les informations client');
      return;
    }
    
    if (!pickup || !dropoff || !date || !time) {
      toast.error('Veuillez remplir tous les détails du trajet');
      return;
    }
    
    if (tripType === 'round-trip' && (!returnDate || !returnTime)) {
      toast.error('Veuillez renseigner la date et l\'heure de retour');
      return;
    }
    
    if (distance === 0) {
      toast.error('Veuillez calculer la distance du trajet');
      return;
    }
    
    setLoading(true);
    
    try {
      const quoteData = {
        customer: {
          name: customerName,
          email: customerEmail,
          phone: customerPhone
        },
        trip: {
          pickup,
          dropoff,
          date,
          time,
          tripType,
          returnDate: tripType === 'round-trip' ? returnDate : null,
          returnTime: tripType === 'round-trip' ? returnTime : null
        },
        vehicle,
        passengers: parseInt(passengers),
        luggage: parseInt(luggage),
        options: [],
        specialNotes: specialNotes || null,
        validityDays: parseInt(validityDays),
        pricing,
        distance,
        baseFare: {
          type: baseFareType,
          amount: pricing.basePrice,
          reason: baseFareType === 'custom' ? baseFareReason : getBaseFareLabel(baseFareType)
        }
      };
      
      const response = await axios.post(`${API}/admin/quotes`, quoteData);
      
      if (response.data.success) {
        toast.success('Devis créé et envoyé avec succès !');
        navigate('/admin/dashboard?tab=quotes');
      }
    } catch (error) {
      console.error('Error creating quote:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création du devis');
    } finally {
      setLoading(false);
    }
  };
  
  const onPickupLoad = useCallback((autocomplete) => {
    setPickupAutocomplete(autocomplete);
  }, []);
  
  const onDropoffLoad = useCallback((autocomplete) => {
    setDropoffAutocomplete(autocomplete);
  }, []);
  
  const onPickupPlaceChanged = () => {
    if (pickupAutocomplete !== null) {
      const place = pickupAutocomplete.getPlace();
      // Use name for airports/stations, otherwise use formatted_address
      let address = place.formatted_address || place.name;
      
      // If it's a point of interest (airport, station, etc.), use name + city
      if (place.name && place.types && (
        place.types.includes('airport') || 
        place.types.includes('train_station') ||
        place.types.includes('bus_station') ||
        place.types.includes('transit_station')
      )) {
        address = place.name;
      }
      
      setPickup(address);
    }
  };
  
  const onDropoffPlaceChanged = () => {
    if (dropoffAutocomplete !== null) {
      const place = dropoffAutocomplete.getPlace();
      // Use name for airports/stations, otherwise use formatted_address
      let address = place.formatted_address || place.name;
      
      // If it's a point of interest (airport, station, etc.), use name + city
      if (place.name && place.types && (
        place.types.includes('airport') || 
        place.types.includes('train_station') ||
        place.types.includes('bus_station') ||
        place.types.includes('transit_station')
      )) {
        address = place.name;
      }
      
      setDropoff(address);
    }
  };
  
  if (!isLoaded) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Chargement...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-8">
      <div className="max-w-5xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/admin/dashboard')}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour au Dashboard
          </Button>
          
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Créer un Devis</h1>
          <p className="text-slate-600">Créez un devis personnalisé pour votre client</p>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="grid gap-6">
            {/* Customer Information */}
            <Card>
              <CardHeader>
                <CardTitle className="text-xl">👤 Informations Client</CardTitle>
              </CardHeader>
              <CardContent className="grid md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="customerName">Nom complet *</Label>
                  <Input
                    id="customerName"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    placeholder="Jean Dupont"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="customerEmail">Email *</Label>
                  <Input
                    id="customerEmail"
                    type="email"
                    value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)}
                    placeholder="jean@example.com"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="customerPhone">Téléphone *</Label>
                  <Input
                    id="customerPhone"
                    type="tel"
                    value={customerPhone}
                    onChange={(e) => setCustomerPhone(e.target.value)}
                    placeholder="+32 123 456 789"
                    required
                  />
                </div>
              </CardContent>
            </Card>
            
            {/* Trip Details */}
            <Card>
              <CardHeader>
                <CardTitle className="text-xl">🚗 Détails du Trajet</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="pickup">Point de départ *</Label>
                    <Autocomplete
                      onLoad={onPickupLoad}
                      onPlaceChanged={onPickupPlaceChanged}
                    >
                      <Input
                        id="pickup"
                        value={pickup}
                        onChange={(e) => setPickup(e.target.value)}
                        placeholder="Entrez l'adresse de départ"
                        required
                      />
                    </Autocomplete>
                  </div>
                  <div>
                    <Label htmlFor="dropoff">Point d'arrivée *</Label>
                    <Autocomplete
                      onLoad={onDropoffLoad}
                      onPlaceChanged={onDropoffPlaceChanged}
                    >
                      <Input
                        id="dropoff"
                        value={dropoff}
                        onChange={(e) => setDropoff(e.target.value)}
                        placeholder="Entrez l'adresse d'arrivée"
                        required
                      />
                    </Autocomplete>
                  </div>
                </div>
                
                <div className="flex items-end gap-4">
                  <div className="flex-1">
                    <Label>Distance calculée</Label>
                    <div className="text-2xl font-bold text-blue-600">
                      {distance > 0 ? `${distance.toFixed(1)} km` : '-'}
                    </div>
                  </div>
                  <Button
                    type="button"
                    onClick={calculateDistance}
                    disabled={calculating || !pickup || !dropoff}
                  >
                    <Calculator className="w-4 h-4 mr-2" />
                    {calculating ? 'Calcul...' : 'Calculer Distance'}
                  </Button>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="date">Date *</Label>
                    <Input
                      id="date"
                      type="date"
                      value={date}
                      onChange={(e) => setDate(e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="time">Heure * (Format 24h : HH:MM)</Label>
                    <input
                      id="time"
                      type="text"
                      value={time}
                      onChange={(e) => setTime(e.target.value)}
                      pattern="([01]?[0-9]|2[0-3]):[0-5][0-9]"
                      placeholder="14:30"
                      required
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      title="Format 24 heures (ex: 14:30)"
                    />
                  </div>
                </div>
                
                <div>
                  <Label>Type de trajet</Label>
                  <div className="flex gap-4 mt-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="tripType"
                        value="one-way"
                        checked={tripType === 'one-way'}
                        onChange={(e) => setTripType(e.target.value)}
                        className="w-4 h-4"
                      />
                      <span>Aller Simple</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="tripType"
                        value="round-trip"
                        checked={tripType === 'round-trip'}
                        onChange={(e) => setTripType(e.target.value)}
                        className="w-4 h-4"
                      />
                      <span>Aller-Retour (-10%)</span>
                    </label>
                  </div>
                </div>
                
                {tripType === 'round-trip' && (
                  <div className="grid md:grid-cols-2 gap-4 p-4 bg-blue-50 rounded-lg">
                    <div>
                      <Label htmlFor="returnDate">Date de retour *</Label>
                      <Input
                        id="returnDate"
                        type="date"
                        value={returnDate}
                        onChange={(e) => setReturnDate(e.target.value)}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="returnTime">Heure de retour * (Format 24h : HH:MM)</Label>
                      <input
                        id="returnTime"
                        type="text"
                        value={returnTime}
                        onChange={(e) => setReturnTime(e.target.value)}
                        pattern="([01]?[0-9]|2[0-3]):[0-5][0-9]"
                        placeholder="16:30"
                        required
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        title="Format 24 heures (ex: 16:30)"
                      />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Vehicle & Passengers */}
            <Card>
              <CardHeader>
                <CardTitle className="text-xl">🚙 Véhicule & Passagers</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="vehicle">Véhicule *</Label>
                  <select
                    id="vehicle"
                    value={vehicle}
                    onChange={(e) => setVehicle(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md"
                    required
                  >
                    {availableVehicles.map((v) => (
                      <option key={v.name} value={v.name}>
                        {v.name} - {v.passengers} passagers, {v.luggageDesc}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="passengers">Nombre de passagers</Label>
                    <Input
                      id="passengers"
                      type="number"
                      min="1"
                      max="8"
                      value={passengers}
                      onChange={(e) => setPassengers(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="luggage">Nombre de bagages</Label>
                    <Input
                      id="luggage"
                      type="number"
                      min="0"
                      max="10"
                      value={luggage}
                      onChange={(e) => setLuggage(e.target.value)}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Pricing Summary */}
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
              <CardHeader>
                <CardTitle className="text-xl text-blue-900">💰 Tarification</CardTitle>
              </CardHeader>
              <CardContent>
                {/* Base Fare Selection */}
                <div className="mb-6 p-4 bg-white rounded-lg border border-blue-300">
                  <h3 className="font-semibold text-blue-900 mb-3">Forfait de Base</h3>
                  <div className="space-y-3">
                    <label className="flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer hover:bg-blue-50 transition-colors" style={{borderColor: baseFareType === 'standard' ? '#3b82f6' : '#e5e7eb'}}>
                      <input
                        type="radio"
                        name="baseFare"
                        value="standard"
                        checked={baseFareType === 'standard'}
                        onChange={(e) => setBaseFareType(e.target.value)}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="font-semibold text-slate-900">Standard Bruxelles - 8€</div>
                        <div className="text-sm text-slate-600">Point de départ dans Bruxelles</div>
                      </div>
                    </label>
                    
                    <label className="flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer hover:bg-blue-50 transition-colors" style={{borderColor: baseFareType === 'enlarged' ? '#3b82f6' : '#e5e7eb'}}>
                      <input
                        type="radio"
                        name="baseFare"
                        value="enlarged"
                        checked={baseFareType === 'enlarged'}
                        onChange={(e) => setBaseFareType(e.target.value)}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="font-semibold text-slate-900">Zone élargie - 30€</div>
                        <div className="text-sm text-slate-600">Brabant wallon/flamand, périphérie proche</div>
                        <div className="text-xs text-blue-600 mt-1">→ Frais de déplacement jusqu'au point de départ inclus</div>
                      </div>
                    </label>
                    
                    <label className="flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer hover:bg-blue-50 transition-colors" style={{borderColor: baseFareType === 'outOfZone' ? '#3b82f6' : '#e5e7eb'}}>
                      <input
                        type="radio"
                        name="baseFare"
                        value="outOfZone"
                        checked={baseFareType === 'outOfZone'}
                        onChange={(e) => setBaseFareType(e.target.value)}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="font-semibold text-slate-900">Hors zone - 50€</div>
                        <div className="text-sm text-slate-600">Flandre, Wallonie (Liège, Namur, Gand, Anvers...)</div>
                        <div className="text-xs text-blue-600 mt-1">→ Frais de déplacement longue distance inclus</div>
                      </div>
                    </label>
                    
                    <label className="flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer hover:bg-blue-50 transition-colors" style={{borderColor: baseFareType === 'custom' ? '#3b82f6' : '#e5e7eb'}}>
                      <input
                        type="radio"
                        name="baseFare"
                        value="custom"
                        checked={baseFareType === 'custom'}
                        onChange={(e) => setBaseFareType(e.target.value)}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="font-semibold text-slate-900">Personnalisé</div>
                        <div className="text-sm text-slate-600 mb-2">Pour cas exceptionnels</div>
                        
                        {baseFareType === 'custom' && (
                          <div className="space-y-2 mt-3">
                            <div>
                              <Label htmlFor="customBaseFare" className="text-sm">Montant du forfait (€)</Label>
                              <Input
                                id="customBaseFare"
                                type="number"
                                min="0"
                                step="0.01"
                                value={customBaseFare}
                                onChange={(e) => setCustomBaseFare(e.target.value)}
                                className="mt-1"
                                placeholder="Ex: 75.00"
                              />
                            </div>
                            <div>
                              <Label htmlFor="baseFareReason" className="text-sm">Raison (visible par le client)</Label>
                              <Input
                                id="baseFareReason"
                                value={baseFareReason}
                                onChange={(e) => setBaseFareReason(e.target.value)}
                                className="mt-1"
                                placeholder="Ex: Déplacement jusqu'à Liège - 80km aller"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </label>
                  </div>
                </div>
                
                {/* Price Breakdown */}
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-slate-700">
                      Forfait de base
                      {baseFareType !== 'standard' && (
                        <span className="text-xs text-blue-600 ml-2">({getBaseFareLabel(baseFareType)})</span>
                      )}
                    </span>
                    <span className="font-semibold">{pricing.basePrice.toFixed(2)} €</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-700">Distance ({distance.toFixed(1)} km)</span>
                    <span className="font-semibold">{pricing.distancePrice.toFixed(2)} €</span>
                  </div>
                  {pricing.discount > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>Remise aller-retour (-10%)</span>
                      <span className="font-semibold">-{pricing.discount.toFixed(2)} €</span>
                    </div>
                  )}
                  <div className="border-t-2 border-blue-300 pt-3 mt-3">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-bold text-blue-900">TOTAL TTC</span>
                      <span className="text-2xl font-bold text-blue-600">{pricing.total.toFixed(2)} €</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Validity & Notes */}
            <Card>
              <CardHeader>
                <CardTitle className="text-xl">⚙️ Options du Devis</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Validité du devis</Label>
                  <div className="flex flex-wrap gap-2 mt-2 mb-3">
                    <Button
                      type="button"
                      variant={validityDays === 3 && !customValidity ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => { setValidityDays(3); setCustomValidity(false); }}
                    >
                      3 jours
                    </Button>
                    <Button
                      type="button"
                      variant={validityDays === 7 && !customValidity ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => { setValidityDays(7); setCustomValidity(false); }}
                    >
                      7 jours
                    </Button>
                    <Button
                      type="button"
                      variant={validityDays === 14 && !customValidity ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => { setValidityDays(14); setCustomValidity(false); }}
                    >
                      14 jours
                    </Button>
                    <Button
                      type="button"
                      variant={validityDays === 30 && !customValidity ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => { setValidityDays(30); setCustomValidity(false); }}
                    >
                      30 jours
                    </Button>
                    <Button
                      type="button"
                      variant={customValidity ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setCustomValidity(true)}
                    >
                      Personnalisé
                    </Button>
                  </div>
                  
                  {customValidity && (
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min="1"
                        max="90"
                        value={validityDays}
                        onChange={(e) => setValidityDays(e.target.value)}
                        className="w-24"
                      />
                      <span className="text-slate-600">jours</span>
                    </div>
                  )}
                  
                  <p className="text-sm text-slate-500 mt-2">
                    Le devis sera valable pendant {validityDays} jour(s)
                  </p>
                </div>
                
                <div>
                  <Label htmlFor="specialNotes">Notes spéciales (optionnel)</Label>
                  <Textarea
                    id="specialNotes"
                    value={specialNotes}
                    onChange={(e) => setSpecialNotes(e.target.value)}
                    placeholder="Conditions particulières, remarques pour le client..."
                    rows={4}
                  />
                </div>
              </CardContent>
            </Card>
            
            {/* Submit Button */}
            <div className="flex justify-end gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/admin/dashboard')}
              >
                Annuler
              </Button>
              <Button
                type="submit"
                disabled={loading || distance === 0}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Send className="w-4 h-4 mr-2" />
                {loading ? 'Envoi en cours...' : 'Créer et Envoyer le Devis'}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminCreateQuote;